/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Formik, Form, Field } from 'formik';
import clsx from 'clsx';
import { bookSlot } from '../../../../../services/appointment';
import slotStyles from './slot_booking_modal.module.scss';
import useLoader from '../../../../../context/utilities/useLoader';
import { useModal } from '../../../../../context/modal';
import {
  fixTooltipPosition,
  validations,
} from '../../../../../helperFunctions';
import { TextField } from '../../../../components/inputs';
import { formValidations } from '../../../../helpers/moduleHelper';
import tooltipStyles from '../../../../../components/users/apptootlip.module.scss';
import Options from './Options';
import usePendoUpdateUrl from '../../../../../hooks/usePendoUpdateUrl';
import InTakeRoleText from '../shared/intake_role_text';
import EapSessionsInfo from '../../../../../components/shared/EapSessionsInfo';
import AppTooltip from '../../../../../components/shared/AppTooltip';
import ParentGuardianInformation from '../../shared/ParentGuardianInformation';
import { getBookAppointmentInformation } from '../../shared/apis';

const appointmentTypes = [
  {
    label: 'Virtual',
    name: 'virtual',
    value: 'virtual',
  },
  {
    label: 'In Person',
    name: 'in_person',
    value: 'in_person',
  },
];
export const GUARDIAN_INITIAL_VALUES = {
  relationship_type: null,
  relationship_type_other: null,
  parent_guardian_first_name: null,
  parent_guardian_last_name: null,
  parent_guardian_email: null,
  parent_guardian_phone_number: null,
  use_parent_guardian_info: true,
};

const SlotBookingModal = ({
  slot,
  closeModal,
  patient,
  encounter,
  survey_id,
  stringValues,
  memberSelfServeRecord,
  filtersApplied = {},
  siteData,
  params,
  referralInstruction,
  allowTextNotification,
  locale,
  dsboConfirmationEnabled,
  dsboCancellationConfirmationEnabled,
  cancellationPollingTimeout,
  eapSessionData,
  virtualVisitBlocked = false,
}) => {
  const [filters, setFilters] = useState(null);
  const [loader, setLoader] = useState(true);
  const [initialValues, setInitialValues] = useState({});

  const patientID = patient?.id;
  const providerID = slot.provider?.id;
  const { notification_details } = patient || {};
  const { sms, email } = notification_details || {};
  const fields = filters?.fields;
  const emailRequired = slot.provider?.email_required;
  const phoneRequired = slot.provider?.phone_required;
  const minorSelfSupported = slot.provider?.minor_self_supported;
  const guardianContactRequired = slot.provider?.guardian_contact_required;
  const orgName = slot.provider?.org_name;
  const showGuardianForm = filters?.data?.show_guardian_form;
  const getAppointmentType = () => {
    if (virtualVisitBlocked) {
      return 'in_person';
    }
    let type = '';
    if (slot.virtual && !slot.in_person) {
      type = 'virtual';
    } else if (!slot.virtual && slot.in_person) {
      type = 'in_person';
    } else if (!filtersApplied.in_person) {
      type = 'virtual';
    } else if (!filtersApplied.virtual) {
      type = 'in_person';
    } else if (slot.provider.out_of_travel_radius) {
      type = 'virtual';
    }
    return type;
  };

  const bookedStatusRef = React.useRef(false);
  const { showLoader, hideLoader } = useLoader({
    key: 'SlotBookingModal',
    displayMessage: stringValues.book_appt_loader_text,
  });
  const { alertDialogue } = useModal();
  const bookAppointment = useCallback(
    (values) => {
      // eslint-disable-next-line no-unused-vars
      const { start_date, end_date, duration } = slot;
      const guardianValues = {};
      if (showGuardianForm) {
        Object.keys(GUARDIAN_INITIAL_VALUES).forEach((gv) => {
          guardianValues[gv] = values[gv];
        });
      }
      showLoader();
      bookSlot({
        slotId: slot.id,
        dsboConfirmationEnabled,
        dsboCancellationConfirmationEnabled,
        cancellationPollingTimeout,
        provider_id: slot.provider.id,
        appointment: {
          start_date,
          duration: duration,
          is_virtual: values.appointment_type === 'virtual',
          site: slot.provider.site,
          site_id: slot.provider.site_id,
          visit_type_id: slot.visit_type_id,
          hasIdentifiers: slot.identifiers,
          visit_type_code: slot.visit_type_code,
          id: slot.id,
        },
        email: values.email,
        sms: values.sms,
        patient_id: patient.id,
        encounter_id: encounter?.id,
        encounterToReschedule: encounter?.id && {
          id: encounter.id,
          hasIdentifiers: encounter.hasIdentifiers,
        },
        member_self_serve_record: memberSelfServeRecord,
        survey_id,
        params,
        guardianParams: guardianValues,
      })
        .then(async (res) => {
          if (res.data?.redirect_path) {
            bookedStatusRef.current = true;
            if (res.data?.redirect_path.includes('/call_centers/providers')) {
              hideLoader();
              // TODO: We need to provide better error messaging here for each case. And instead of a reload, we should call the API again.
              await alertDialogue(
                'We apologize for the inconvenience. Something unexpected occurred. Please try again.',
                { className: 'mss-pendo-booking_alert_unexpected_error' },
              );
              window.location.reload();
            } else {
              window.location.href = res?.data?.redirect_path;
            }
          } else {
            hideLoader();
            closeModal(res);
          }
        })
        .catch(async (err) => {
          hideLoader();
          if (err?.message === 'Could not cancel the external appointment.') {
            await alertDialogue(
              'We’re sorry, but your original appointment can no longer be modified by Lucet. Please contact the provider directly for any appointment changes needed.',
            );
            window.location.href = `/member_self_serve/${memberSelfServeRecord}`;
          } else if (err) {
            // Prefer the structured server error when the backend returned one (422 with error body)
            const serverError = err.response?.data?.error;
            const isNetworkError = !err.response && err.message === 'Network Error';
            const pendoErrorTag = isNetworkError
              ? 'mss-pendo-booking_alert_network_error'
              : (serverError?.pendo_error_tag
                  ? `mss-pendo-${serverError.pendo_error_tag}`
                  : err.pendoErrorTag ||
                    (err.response?.status === 422 ? 'booking_alert_status_422' : ''));
            const displayMessage =
              (serverError?.display_in_ui && serverError?.message) ||
              err.message ||
              'Request Failed. Please try again.';
            await alertDialogue(
              displayMessage,
              pendoErrorTag ? { className: pendoErrorTag } : {},
            );
          } else {
            alert(err);
          }
        });
    },
    [slot, survey_id, patient.id, filters],
  );

  useEffect(() => {
    const sendUnlockBeacon = () => {
      const payload = new FormData();
      payload.append('locked_for_id', patient.id);
      payload.append('locked_for_type', 'Patient');
      if (
        bookedStatusRef.current === false &&
        navigator &&
        typeof navigator.sendBeacon === 'function'
      ) {
        navigator.sendBeacon(`/slots/${slot.id}/unlock/`, payload);
      }
    };

    window.addEventListener('beforeunload', sendUnlockBeacon);
    return () => {
      window.removeEventListener('beforeunload', sendUnlockBeacon);
    };
  }, [slot.id, patient.id]);

  usePendoUpdateUrl(`/modal/slot_booking_modal/${slot.id}`);

  let postFixTitle = '';
  if (referralInstruction && referralInstruction.name) {
    postFixTitle = `: ${referralInstruction.name}`;
  }

  const bothPhoneEmailAreNotRequired = () =>
    emailRequired === false && phoneRequired === false;

  const validateEmailPhone = ({ values, emailField, phoneField }) => {
    const sectionErrors = {};
    const {
      email: emailValidation,
      phone: phoneValidation,
      required: requiredValidation,
    } = validations;
    const emailAndPhoneNotRequired = bothPhoneEmailAreNotRequired();
    const requiredErrorMessage = formValidations.fieldErrorMessage(locale);
    const emailOrPhoneMessage =
      formValidations.emailOrPhoneErrorMessage(locale);

    const emailEmpty = requiredValidation(values[emailField]);
    const phoneEmpty = requiredValidation(values[phoneField]);

    /* ---------------- Required Rules ---------------- */

    if (emailRequired && emailEmpty) {
      sectionErrors[emailField] = requiredErrorMessage;
    }

    if (phoneRequired && allowTextNotification && phoneEmpty) {
      sectionErrors[phoneField] = requiredErrorMessage;
    }

    /* ---------------- At Least One Rule ---------------- */

    if (emailAndPhoneNotRequired && emailEmpty && phoneEmpty) {
      if (allowTextNotification) {
        sectionErrors[phoneField] = emailOrPhoneMessage;
      } else {
        sectionErrors[emailField] = requiredErrorMessage;
      }
    }

    /* ---------------- Format Validation ---------------- */

    if (!sectionErrors[emailField]) {
      const emailError = emailValidation(values[emailField], locale);
      if (emailError) {
        sectionErrors[emailField] = emailError;
      }
    }

    if (allowTextNotification && !sectionErrors[phoneField]) {
      const phoneError = phoneValidation(values[phoneField], locale);
      if (phoneError) {
        sectionErrors[phoneField] = phoneError;
      }
    }

    return sectionErrors;
  };

  const validate = (values) => {
    let errors = {};
    const requiredError = formValidations.fieldErrorMessage(locale);

    if (
      siteData.show_warning_message === true &&
      !values.out_of_state_confirmation
    ) {
      errors.out_of_state_confirmation = requiredError;
    }

    if (validations.required(values.appointment_type)) {
      errors.appointment_type = requiredError;
    }
    const { required: requiredValidation } = validations;

    if (showGuardianForm) {
      const options = fields?.relationship_type?.options || [];
      const selection = options.find(
        (o) => o.value === values.relationship_type,
      );

      const isSelf = selection?.title === 'self';
      const isOther = selection?.title === 'other';

      const applyError = (key, err) => {
        if (err) errors[key] = err;
      };

      applyError(
        'relationship_type',
        requiredValidation(values.relationship_type, locale),
      );

      if (!isSelf) {
        if (isOther) {
          applyError(
            'relationship_type_other',
            requiredValidation(values.relationship_type_other, locale),
          );
        }

        applyError(
          'parent_guardian_first_name',
          requiredValidation(values.parent_guardian_first_name, locale),
        );
        applyError(
          'parent_guardian_last_name',
          requiredValidation(values.parent_guardian_last_name, locale),
        );

        errors = {
          ...errors,
          ...validateEmailPhone({
            values,
            emailField: 'parent_guardian_email',
            phoneField: 'parent_guardian_phone_number',
          }),
        };
      }
    }

    if (values.use_parent_guardian_info !== true) {
      errors = {
        ...errors,
        ...validateEmailPhone({
          values,
          emailField: 'email',
          phoneField: 'sms',
        }),
      };
    }

    return errors;
  };

  const handleSubmit = (values) => {
    bookAppointment(values);
  };

  useEffect(() => {
    if (!patientID || !providerID) return;

    setLoader(true);

    getBookAppointmentInformation('member_self_serve', {
      patient_id: patientID,
      practitioner_id: providerID,
      page: 'mss',
      locale,
    })
      .then((res) => {
        const result = res.data || {};
        const showForm = result?.data?.show_guardian_form === true;

        setFilters(result);
        setInitialValues({
          email: email?.contact_value || '',
          sms: allowTextNotification ? sms?.contact_value || '' : '',
          appointment_type: getAppointmentType() || '',
          ...(showForm ? GUARDIAN_INITIAL_VALUES : {}),
          ...(showForm && guardianContactRequired
            ? {
                use_parent_guardian_info: true,
                parent_guardian_confirm_email: true,
                parent_guardian_confirm_phone_number: true,
              }
            : {}),
        });
      })
      .catch((err) => {
        console.error('Error fetching appointment info:', err);
        setFilters({});
        setInitialValues({
          email: email?.contact_value || '',
          sms: allowTextNotification ? sms?.contact_value || '' : '',
          appointment_type: getAppointmentType() || '',
        });
      })
      .finally(() => {
        setLoader(false);
      });
  }, [patientID, providerID]);

  return (
    <Formik
      enableReinitialize
      validateOnMount
      validateOnChange
      validate={validate}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          handleSubmit(values);
          setSubmitting(false);
        }, 100);
      }}
    >
      {({ values, isValid, isSubmitting, setFieldValue }) => (
        <Form>
          <div
            className={`${slotStyles.slotContainer} align-center col-center mss-slot-booking-modal-wrapper`}
          >
            {siteData.show_warning_message === true && (
              <div className={`${slotStyles.warning} padding-md`}>
                <span
                  className={clsx(
                    slotStyles.outOfStateConfirmationCheckBox,
                    'checkbox-wrap',
                  )}
                >
                  <Field
                    id="out_of_state_confirmation"
                    type="checkbox"
                    name="out_of_state_confirmation"
                  />
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label htmlFor="out_of_state_confirmation">
                    <span className="bold">{stringValues.warning}: </span>
                    {stringValues.out_of_state_confirmation}
                  </label>
                </span>
              </div>
            )}
            <div className={`padding-lg ${slotStyles.slotBookingBody}`}>
              <h1
                className={`prompt ${slotStyles.slotHeading}`}
                id="dialog_head"
              >
                {encounter?.start_date
                  ? `Reschedule ${moment(encounter?.start_date).format(
                      'M/D/YYYY h:mma',
                    )} Appointment to${postFixTitle}`
                  : `${stringValues.book_appointment}${postFixTitle}`}
              </h1>
              <hr className={slotStyles.slotHeadingUnderline} />
              <div
                id="dialog_desc"
                className={
                  !slot.in_person || !slot.provider ? 'margin-bottom-xl' : ''
                }
              >
                <h2 className={`${slotStyles.slotText} ${!fields?.end_date?.visible ? slotStyles.slotTextNoEndDate : ''} prompt`}>
                  <span>
                    {
                      stringValues[
                        moment(slot.start_date).format('dddd').toLowerCase()
                      ]
                    }
                    {',  '}
                    {
                      stringValues[
                        moment(slot.start_date).format('MMMM') === 'May'
                          ? 'may_full'
                          : moment(slot.start_date).format('MMMM').toLowerCase()
                      ]
                    }{' '}
                    {moment(slot.start_date).format('D')}
                  </span>
                  <span>
                    {moment(slot.start_date).format('h:mm a')}
                    {fields?.end_date?.visible && ` – ${moment(slot.end_date).format('h:mm a')}`}
                  </span>
                </h2>
                <div className={`${slotStyles.fieldsetWrapper}`}>
                  <fieldset
                    className={`${slotStyles.fieldset} flex-wrap-children justify-content-center`}
                  >
                    <Options
                      name="appointment_type"
                      options={appointmentTypes || []}
                      slot={slot}
                      filtersApplied={filtersApplied}
                      stringValues={stringValues}
                      slotStyles={slotStyles}
                      value={values.appointment_type}
                      virtualVisitBlocked={virtualVisitBlocked}
                    />
                  </fieldset>
                  <InTakeRoleText
                    provider={slot.provider}
                    stringValues={stringValues}
                    isBookingModal
                  />
                  <EapSessionsInfo
                    eapSessionData={eapSessionData}
                    locale={locale}
                    isMss
                  />
                  {showGuardianForm && (
                    <ParentGuardianInformation
                      values={values}
                      filters={filters}
                      emailRequired={emailRequired || guardianContactRequired}
                      phoneRequired={phoneRequired || guardianContactRequired}
                      guardianContactRequired={guardianContactRequired}
                      orgName={orgName}
                      setFieldValue={setFieldValue}
                      styles={slotStyles}
                    />
                  )}
                  {!loader &&
                    (!showGuardianForm ||
                      !values.use_parent_guardian_info ||
                      (fields?.relationship_type?.options || []).find(
                        (o) => o.value === values.relationship_type,
                      )?.title === 'self') && (
                      <section className={slotStyles.section}>
                        {showGuardianForm && (
                          <div className="flex">
                            <h4 className={slotStyles.sectionTitles}>
                              {fields?.notification_header?.label}
                            </h4>
                          </div>
                        )}
                        <div className={slotStyles.fieldsetWrapper}>
                          <div className={slotStyles.fieldsWrapper}>
                            {emailRequired &&
                              phoneRequired &&
                              allowTextNotification && (
                                <div
                                  className={`${slotStyles.validationWarningMessage} margin-top-lg`}
                                >
                                  <span>
                                    {fields?.email_phone_warning?.label}
                                  </span>
                                </div>
                              )}
                            <div>
                              <TextField
                                name="email"
                                type="text"
                                placeholder={fields?.email?.placeholder}
                                mask="email"
                              />
                            </div>
                          </div>
                          {allowTextNotification && (
                            <div className={slotStyles.textFieldsWrapper}>
                              <TextField
                                name="sms"
                                type="text"
                                placeholder={fields?.sms?.placeholder}
                                mask="phone"
                              />
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                </div>
              </div>
              <div className="button-section margin-top-lg flex justify-end">
                <button
                  type="button"
                  className={`${slotStyles.slotBtn} cancel-btn`}
                  onClick={closeModal}
                >
                  {stringValues.cancel}
                </button>
                {(() => {
                  const options = fields?.relationship_type?.options || [];
                  const selection = options.find(
                    (o) => o.value === values.relationship_type,
                  );
                  const isMinorSelfBlocked =
                    showGuardianForm &&
                    minorSelfSupported === false &&
                    selection?.title === 'self';
                  const minorSelfBlockedReason = isMinorSelfBlocked
                    ? `${
                        orgName || 'This provider org'
                      } does not support "Self (patient can legally consent)".`
                    : '';
                  const minorSelfBlockedTooltipId =
                    'mss-minor-self-blocked-tooltip';
                  return (
                    <span
                      className={minorSelfBlockedReason ? 'tool-tip' : ''}
                      data-tip={minorSelfBlockedReason ? true : undefined}
                      data-for={
                        minorSelfBlockedReason
                          ? minorSelfBlockedTooltipId
                          : undefined
                      }
                      data-class={
                        minorSelfBlockedReason ? 'tooltip' : undefined
                      }
                    >
                      <button
                        type="submit"
                        className={`${slotStyles.slotBtn} success-btn upcase`}
                        disabled={
                          isValid !== true ||
                          isSubmitting === true ||
                          loader ||
                          isMinorSelfBlocked
                        }
                      >
                        {encounter?.start_date
                          ? 'Reschedule Appointment'
                          : stringValues.book_appointment}
                      </button>
                      {minorSelfBlockedReason && (
                        <AppTooltip
                          id={minorSelfBlockedTooltipId}
                          className={`tooltip ${tooltipStyles.appTooltipReact} ${tooltipStyles.smSize} ${tooltipStyles.withWrap} text-center`}
                          arrowColor="#222"
                          place="bottom"
                          overridePosition={fixTooltipPosition}
                          multiline
                        >
                          {minorSelfBlockedReason}
                        </AppTooltip>
                      )}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
export default SlotBookingModal;
